import dayjs from 'dayjs'

import {
  computeHouseAvailabilityState,
  ComputeIsAvailabilityLockedProps,
} from './compute-house-availability-state.helper'
import { getAvailabilityFromDate } from './get-availability-from-date.helper'
import { DatesCalendarProps } from '../dates-calendar'
import { IDates } from '../useTimezoneConverter'
import {
  HouseAvailabilityUnionOutputDto,
  TravelTypeEnum,
} from '../../../../lib/graphql'
import { convertTimezonedDateToIsoAtMidnight as utcStartOfDay } from '../../../../utils/date-helpers'

export type ComputeTileDisabledProps = {
  date: Date
  travelType: TravelTypeEnum | null
  house: DatesCalendarProps['house']
  houseAvailabilities: HouseAvailabilityUnionOutputDto[]
  startDate: IDates['startDate']
  endDate: IDates['endDate']
}

export const isTileDisabled = (props: ComputeTileDisabledProps): boolean => {
  const date = utcStartOfDay(props.date)

  const { houseAvailabilities, house } = props

  if (!house) {
    return false
  }

  const houseAvailability = getAvailabilityFromDate({
    date,
    houseAvailabilities,
  })

  const isTileLocked = houseAvailability
    ? computIsTileLocked({ ...props, houseAvailability })
    : false

  return isTileLocked || isTileWithoutAvailabilityDisabled(props)
}

const computIsTileLocked = (
  props: ComputeIsAvailabilityLockedProps,
): boolean => {
  const { isCorporateEveningLocked, isNightlyTripLocked, isStudyDayLocked } =
    computeHouseAvailabilityState(props)

  const isFullyLocked =
    isCorporateEveningLocked && isNightlyTripLocked && isStudyDayLocked

  if (isFullyLocked) {
    return true
  }

  const { travelType } = props

  return (
    (travelType === TravelTypeEnum.CORPORATE_EVENING_EVENT &&
      isCorporateEveningLocked) ||
    (travelType === TravelTypeEnum.NIGHTLY_TRIP && isNightlyTripLocked) ||
    (travelType === TravelTypeEnum.DAILY_TRIP && isStudyDayLocked)
  )
}

const isTileWithoutAvailabilityDisabled = ({
  startDate,
  endDate,
  houseAvailabilities,
  date,
  travelType,
}: ComputeTileDisabledProps): boolean => {
  const previousAvailabilities = houseAvailabilities
    .filter((houseAvailability) =>
      dayjs(houseAvailability.date).isBefore(startDate),
    )
    .sort((a, b) => dayjs(b.date).diff(dayjs(a.date))) // Sort from youngest to oldest

  const previousLockedAvailability = previousAvailabilities.find(
    (houseAvailability) =>
      computIsTileLocked({ houseAvailability, travelType }),
  )

  const nextLockedAvailability = houseAvailabilities.find(
    (houseAvailability) =>
      dayjs(houseAvailability.date).isAfter(startDate) &&
      computIsTileLocked({ houseAvailability, travelType }),
  )

  const shouldBeDisabled = Boolean(
    startDate &&
      !endDate &&
      ((nextLockedAvailability &&
        !dayjs(date).isBefore(nextLockedAvailability.date)) ||
        (previousLockedAvailability &&
          !dayjs(date).isAfter(previousLockedAvailability.date))),
  )

  return shouldBeDisabled
}
